import { formatDistanceToNow } from 'date-fns';
import {action, computed, observable} from 'mobx';
import { observer } from 'mobx-react';
import {
    autoinject, bind,
    displayFormatTime,
    inject, ModalHandle,
    ServicesAlert,
    SoftwareUpdateAlert,
    Title,
    Tooltip,
} from 'Mri.Web.Common';
import * as React from 'react';
import { LinkContainer } from 'react-router-bootstrap';
import { ContractsStore } from '../../Contracts/ContractsStore';
import { AppStore } from '../../Core/AppStore';
import { DisplaysStore } from '../DisplaysStore';
import { BsColumns } from '@react-icons/all-files/bs/BsColumns';
import {ContractModal} from "../../Contracts/ContractModal";

export interface IDisplayViewTitleProps {
    readonly serialNumber: string;
}

@autoinject
@observer
export class DisplayViewTitle extends React.Component<IDisplayViewTitleProps> {
    public async componentDidMount() {
        try {
            await this.displaysStore.loadAsync(this.props.serialNumber, {
                params: {
                    status: true,
                    contract: true,
                },
            });
        } catch (e: any) {
            this.appStore.setError(e);
        }
    }

    public render() {
        if (!this.display || !this.display.statusMessageBrief) return <Title>{this.props.serialNumber}</Title>;
        return (
            <Title documentTitle={this.props.serialNumber}>
                <div className="d-inline-block fw-bold px-2">{this.props.serialNumber}</div>

                <div className="d-inline-block">
                    <SoftwareUpdateAlert className="ps-2" updateStatus={this.display.displaySoftwareUpdateStatus} />

                    <ServicesAlert onClick={this.openContractModal} className="p-2 f-12" contract={this.contract} display={this.display} />
                </div>

                {this.warehouse && (
                    <Tooltip message={`Display is in the ${this.warehouse.name} Warehouse`}>
                        <LinkContainer to="/warehouses" className="p-0 btn btn-link">
                            <BsColumns />
                        </LinkContainer>
                    </Tooltip>
                )}

                <div className="d-none d-md-inline-block px-2">
                    <div className="row">
                        <div className="col-12">
                            <small className={this.statusClassName}>{this.display.statusMessageBrief}</small>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-12">
                            <small>
                                Updated:{' '}
                                {this.display.pollTime
                                    ? displayFormatTime(
                                        this.display.pollTime,
                                        'EEEE, MMMM do yyyy, h:mm:ss a ZZZZ',
                                        this.props.serialNumber
                                    )
                                    : '-'}
                            </small>
                            &nbsp;
                            {this.elapsedPollTime && (
                                <small className={this.pollTimeClassName}>{this.elapsedPollTime}</small>
                            )}
                        </div>
                    </div>
                </div>
                <div className="d-none d-md-inline-block">
                    <ServicesAlert onClick={this.openContractModal} className="p-2" type="message" contract={this.contract} display={this.display} />
                </div>

                {this.contract && (
                    <ContractModal
                        contract={this.contract}
                        display={this.display}
                        modalHandle={this.contractModalHandle}
                        viewState="readOnly"
                    />
                )}
            </Title>
        );
    }

    @bind
    @action
    private openContractModal() {
        this.contractModalHandle.open();
    }

    @computed
    private get display() {
        return this.displaysStore.displays.get(this.props.serialNumber);
    }

    @computed
    private get statusClassName() {
        if (!this.display) return;

        switch (this.display.state) {
            case 'good':
                return 'text-default';
            case 'exception':
                return 'text-info';
            case 'warning':
                return 'text-warning';
            case 'critical':
                return 'text-danger';
        }
    }

    @computed
    private get elapsedSecondsSincePoll() {
        if (!this.display || !(this.display.pollTime instanceof Date)) return;
        else return (this.appStore.clock.time.getTime() - this.display.pollTime.getTime()) / 1000;
    }

    @computed
    private get elapsedPollTime() {
        const pollTime = this.display!.pollTime;
        const now = new Date();

        if (pollTime && pollTime < now) {
            return formatDistanceToNow(pollTime, { addSuffix: true });
        }
        if (pollTime && pollTime > now) {
            return 'A few seconds ago';
        }
        return '';
    }

    @computed
    private get pollTimeClassName() {
        if (this.elapsedSecondsSincePoll && this.elapsedSecondsSincePoll > 18000) return 'text-danger';
        else return 'text-default';
    }

    @computed
    private get contract() {
        if (this.display?.contract) return this.contractsStore.contracts.get(this.display.contract);
    }

    @computed
    private get warehouse() {
        return this.displaysStore.warehouses.get(this.props.serialNumber);
    }

    private contractModalHandle = new ModalHandle();
    @inject
    private contractsStore!: ContractsStore;
    @inject
    private displaysStore!: DisplaysStore;
    @inject
    private appStore!: AppStore;
}
